import { Nav } from '../components/Nav'
import { Link } from 'react-router-dom'

export default function Privacy() {
  const lastUpdated = 'January 2, 2026'

  return (
    <div className="page">
      <div className="page-header">
        <Link to="/settings" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontSize: '0.875rem' }}>
          ← Back to Settings
        </Link>
        <h1 className="page-title" style={{ marginTop: '0.5rem' }}>Privacy Policy</h1>
        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          Last updated: {lastUpdated}
        </p>
      </div>

      <div className="page-content" style={{ paddingBottom: '100px' }}>
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Overview</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            Reframer ("we," "our," or "us") is committed to protecting your privacy. This policy explains
            how we collect, use, and safeguard your information when you use the Reframer application
            at presentlens.app.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Information We Collect</h2>

          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', marginTop: '1rem' }}>Session Data</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
            When you use Reframer, we collect:
          </p>
          <ul style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
            <li>Text you enter describing your thoughts or situations</li>
            <li>AI-generated reframes and insights based on your input</li>
            <li>Emotion ratings you provide (before/after)</li>
            <li>Timestamps of your sessions</li>
          </ul>

          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', marginTop: '1rem' }}>Voice Recordings</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            When using voice mode, your speech is processed in real-time using Cloudflare Workers AI
            for transcription. By default, audio is <strong>not stored</strong> after transcription.
            If you enable "Audio Storage" in settings, encrypted recordings are stored for your review.
          </p>

          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', marginTop: '1rem' }}>Anonymous Accounts</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            Reframer uses anonymous accounts by default. We generate a random identifier for your device
            but do not collect your name, email, or other personally identifying information unless you
            choose to provide it.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>How We Use Your Information</h2>
          <ul style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
            <li><strong>To provide the service:</strong> Your input is processed by AI to generate cognitive reframes</li>
            <li><strong>To show your history:</strong> Past sessions are stored so you can review your progress</li>
            <li><strong>To generate insights:</strong> Patterns in your sessions help provide personalized insights</li>
            <li><strong>To detect crisis language:</strong> We analyze input for safety-related keywords to show crisis resources when needed</li>
          </ul>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Crisis Detection</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            Reframer includes a safety feature that detects language indicating potential crisis or
            self-harm. When detected, we display crisis resources (such as the 988 Suicide & Crisis Lifeline).
            This detection happens locally in the app and <strong>we do not report or share this information</strong>
            with any third parties, law enforcement, or healthcare providers.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Data Storage & Security</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
            Your data is stored securely using:
          </p>
          <ul style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
            <li><strong>Cloudflare D1:</strong> A serverless SQL database with encryption at rest</li>
            <li><strong>Cloudflare R2:</strong> Object storage for audio files (if enabled), with encryption</li>
            <li><strong>HTTPS:</strong> All data in transit is encrypted</li>
          </ul>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6, marginTop: '0.75rem' }}>
            Data is stored in Cloudflare's global network and processed in accordance with
            their security practices.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>AI Processing</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            We use Cloudflare Workers AI to process your input and generate reframes. Your data is
            processed in Cloudflare's infrastructure and is <strong>not used to train AI models</strong>.
            AI processing happens in real-time and inputs are not retained by the AI service after
            generating a response.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Your Rights</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
            You have the right to:
          </p>
          <ul style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
            <li><strong>Export your data:</strong> Download all your sessions and journal entries in JSON format from Settings</li>
            <li><strong>Delete your data:</strong> Permanently delete all your data from our servers via Settings</li>
            <li><strong>Control audio storage:</strong> Toggle whether voice recordings are saved in Settings</li>
          </ul>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>What We Don't Do</h2>
          <ul style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem' }}>
            <li>We do <strong>not</strong> sell your data to third parties</li>
            <li>We do <strong>not</strong> share your data with advertisers</li>
            <li>We do <strong>not</strong> use your data for advertising or marketing</li>
            <li>We do <strong>not</strong> share data with healthcare providers or insurers</li>
            <li>We do <strong>not</strong> use your inputs to train AI models</li>
          </ul>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Third-Party Services</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            Reframer uses Cloudflare for hosting, database, storage, and AI processing.
            Cloudflare's privacy policy applies to their handling of data:{' '}
            <a
              href="https://www.cloudflare.com/privacypolicy/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)' }}
            >
              cloudflare.com/privacypolicy
            </a>
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Children's Privacy</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            Reframer is not intended for children under 13. We do not knowingly collect personal
            information from children under 13. If you believe a child has provided us with personal
            information, please contact us.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Changes to This Policy</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            We may update this privacy policy from time to time. We will notify you of significant
            changes by posting a notice in the app. Continued use of Reframer after changes
            constitutes acceptance of the updated policy.
          </p>
        </div>

        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', marginBottom: '0.75rem' }}>Contact Us</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            If you have questions about this privacy policy or your data, please contact us at:{' '}
            <a
              href="mailto:privacy@presentlens.app"
              style={{ color: 'var(--color-primary)' }}
            >
              privacy@presentlens.app
            </a>
          </p>
        </div>
      </div>

      <Nav />
    </div>
  )
}
